import * as React from 'react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Avatar } from './avatar'
import type { ProfileType } from 'shared'
import { useIsMobile } from '@/hooks/use-mobile'
import { Skeleton } from '@/components/ui/skeleton'
import { FetchingProgress } from './fetching-progress'

// Cookie configuration
const PANEL_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

interface CollapsiblePanelContextProps {
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
  togglePanel: () => void
}

const CollapsiblePanelContext = React.createContext<CollapsiblePanelContextProps | null>(null)

function useCollapsiblePanel() {
  const context = React.useContext(CollapsiblePanelContext)
  if (!context) {
    throw new Error('useCollapsiblePanel must be used within a CollapsiblePanelProvider')
  }
  return context
}

/** Non-throwing variant — returns null when not inside a CollapsiblePanelProvider. */
export function useOptionalCollapsiblePanel() {
  return React.useContext(CollapsiblePanelContext)
}

export function CollapsiblePanelProvider(props: {
  children: React.ReactNode
  defaultCollapsed?: boolean
  cookieName?: string
  keyboardShortcut?: string
}) {
  const cookieName = props.cookieName ?? 'panel_state_main'
  const keyboardShortcut = props.keyboardShortcut ?? '['

  const [isCollapsed, setIsCollapsedState] = React.useState(() => {
    if (typeof document === 'undefined') return props.defaultCollapsed ?? false

    // If defaultCollapsed is explicitly provided, use it (for mobile responsiveness)
    // Otherwise, read from cookie
    if (props.defaultCollapsed !== undefined) {
      return props.defaultCollapsed
    }

    const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${cookieName}=`))
    if (cookie) {
      return cookie.split('=')[1] === 'true'
    }
    return false
  })

  // react-doctor-disable-next-line react-doctor/react-compiler-no-manual-memoization -- kept stable: feeds togglePanel's deps + the keyboard-shortcut effect; removing it trips react-hooks/exhaustive-deps
  const setIsCollapsed = React.useCallback(
    (collapsed: boolean) => {
      setIsCollapsedState(collapsed)
      document.cookie = `${cookieName}=${collapsed}; path=/; max-age=${PANEL_COOKIE_MAX_AGE}`
    },
    [cookieName],
  )

  // react-doctor-disable-next-line react-doctor/react-compiler-no-manual-memoization -- kept stable: consumed in the keyboard-shortcut useEffect dep array; removing it trips react-hooks/exhaustive-deps
  const togglePanel = React.useCallback(() => {
    setIsCollapsed(!isCollapsed)
  }, [isCollapsed, setIsCollapsed])

  // Re-sync collapsed state when the defaultCollapsed prop changes (e.g. viewport resize), inline
  // during render via a prev-prop comparison instead of an effect — an effect would force an extra
  // render showing the stale collapsed state between the two commits.
  // react-doctor-disable-next-line react-doctor/rerender-state-only-in-handlers -- prev-prop tracker for the render-time adjustment below; read for comparison, intentionally not rendered
  const [prevDefaultCollapsed, setPrevDefaultCollapsed] = React.useState(props.defaultCollapsed)
  if (props.defaultCollapsed !== undefined && props.defaultCollapsed !== prevDefaultCollapsed) {
    setPrevDefaultCollapsed(props.defaultCollapsed)
    setIsCollapsedState(props.defaultCollapsed)
  }

  // Keyboard shortcut
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === keyboardShortcut && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        togglePanel()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [togglePanel, keyboardShortcut])

  const contextValue = React.useMemo(
    () => ({ isCollapsed, setIsCollapsed, togglePanel }),
    [isCollapsed, setIsCollapsed, togglePanel],
  )

  return <CollapsiblePanelContext.Provider value={contextValue}>{props.children}</CollapsiblePanelContext.Provider>
}

export function CollapsiblePanel(props: {
  children: React.ReactNode
  className?: string
  expandedWidth?: string
  collapsedWidth?: string
  disableTransition?: boolean
}) {
  const { isCollapsed } = useCollapsiblePanel()

  return (
    <div
      className={cn(
        'relative h-full shrink-0 border-r overflow-hidden flex flex-col',
        !props.disableTransition && 'transition-[width] duration-200 ease-in-out',
        props.className,
      )}
      style={{ width: isCollapsed ? (props.collapsedWidth ?? '52px') : (props.expandedWidth ?? '450px') }}
    >
      {props.children}
    </div>
  )
}

export function CollapsiblePanelContent(props: { children: React.ReactNode; className?: string }) {
  const { isCollapsed } = useCollapsiblePanel()

  return (
    <div
      className={cn(
        'absolute inset-0 flex flex-col transition-opacity duration-200',
        isCollapsed && 'opacity-0',
        props.className,
      )}
      inert={isCollapsed}
      aria-hidden={isCollapsed || undefined}
    >
      {props.children}
    </div>
  )
}

export function CollapsedPanelView(props: { children: React.ReactNode; className?: string }) {
  const { isCollapsed } = useCollapsiblePanel()

  // flex-1 min-h-0 keeps this in normal flow so it gets a proper height from the
  // flex column parent (CollapsiblePanel) regardless of whether the containing block
  // has an explicit or auto height — the same pattern ScrollableList uses.
  // CollapsiblePanelContent is the absolute overlay that crossfades on top.
  return (
    <div
      inert={!isCollapsed}
      aria-hidden={!isCollapsed || undefined}
      className={cn(
        'flex-1 min-h-0 z-10 flex flex-col overflow-hidden transition-opacity duration-200',
        !isCollapsed && 'opacity-0',
        props.className,
      )}
    >
      {props.children}
    </div>
  )
}

export function CollapsedPanelHeader(props: { children?: React.ReactNode; className?: string }) {
  const isMobile = useIsMobile()
  if (isMobile) return null
  return <div className={cn('flex flex-col items-center gap-1 border-b py-3', props.className)}>{props.children}</div>
}

/**
 * Container for collapsed panel items with built-in support for loading, empty states, and infinite scroll.
 * Similar to ScrollableList but optimized for collapsed panel layouts with centered items.
 *
 * @example
 * ```tsx
 * <CollapsedPanelItems
 *   isLoading={isLoading}
 *   isEmpty={items.length === 0}
 *   emptyState={<EmptyIcon />}
 *   isFetchingMore={isFetchingNextPage}
 *   loadMoreRef={loadMoreRef}
 * >
 *   {items.map(item => <CollapsedPanelItem key={item.id} {...item} />)}
 * </CollapsedPanelItems>
 * ```
 */
export function CollapsedPanelItems(props: {
  children: React.ReactNode
  className?: string
  /** Show loading skeleton placeholders */
  isLoading?: boolean
  /** Show fetching progress indicator during refetch */
  isFetching?: boolean
  /** Show fetching progress indicator at the top */
  isFetchingMore?: boolean
  /** Ref for infinite scroll trigger element */
  loadMoreRef?: React.Ref<HTMLDivElement>
  /** Content to show when list is empty */
  emptyState?: React.ReactNode
  /** Whether the list is empty */
  isEmpty?: boolean
}) {
  return (
    <div className={cn('relative flex-1 min-h-0 overflow-auto hide-scrollbar', props.className)}>
      <div className="px-2">
        {props.isLoading ? (
          <div className="flex flex-col items-center gap-2 py-3">
            <Skeleton key="skeleton-0" className="size-8 rounded-md" />
            <Skeleton key="skeleton-1" className="size-8 rounded-md" />
            <Skeleton key="skeleton-2" className="size-8 rounded-md" />
          </div>
        ) : props.isEmpty && props.emptyState ? (
          <div className="pt-4">{props.emptyState}</div>
        ) : (
          <>
            <FetchingProgress isFetching={props.isFetching || props.isFetchingMore} className="-mx-2" />
            <div className="flex flex-col items-center gap-2 py-3">{props.children}</div>
            {props.loadMoreRef && <div ref={props.loadMoreRef} />}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Collapsed panel item that wraps content in a custom link component.
 * Use this when you need TanStack Router's Link component.
 */
export function CollapsedPanelLinkItem(props: {
  /** Display name for tooltip and avatar fallback */
  name: string
  /** Avatar image URL */
  imageUrl?: string | null
  /** Avatar type for color/style */
  type: ProfileType
  /** Avatar variant (e.g., 'job') */
  variant?: 'job'
  /** Whether this item is currently selected */
  isSelected?: boolean
  /** Render prop for custom link wrapper (for TanStack Router Link) */
  children: (content: React.ReactNode) => React.ReactNode
  /** Rich tooltip content - if provided, will be used instead of just the name */
  tooltipContent?: React.ReactNode
  className?: string
  /** Avatar size */
  size?: 'sm' | 'md'
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-selected={props.isSelected || undefined}
          className={cn(
            'inline-flex rounded-sm transition-colors hover:bg-muted',
            props.isSelected && 'bg-primary/10 hover:bg-primary/10',
            props.className,
          )}
        >
          {props.children(
            <Avatar
              size={props.size ?? 'md'}
              url={props.imageUrl}
              name={props.name}
              type={props.type}
              variant={props.variant}
            />,
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-none">
        {props.tooltipContent ?? props.name}
      </TooltipContent>
    </Tooltip>
  )
}
